import React, { Fragment, PropsWithChildren } from 'react';
import { Link } from '@tanstack/react-router';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { Button } from '@progress/kendo-react-buttons';
import { ScrollView } from '@progress/kendo-react-scrollview';
import { getFormattedDate, truncateStringAtWhitespace } from './util.ts';
import { SignalLight } from './SignalLight.tsx';
import { restApiUrl } from '../shared/helpers/backendUrl';

interface SeminarPrice {
  price?: number | null;
  priceText?: string | null;
  price2?: number | null;
  priceText2?: string | null;
  price3?: number | null;
  priceText3?: string | null;
  price4?: number | null;
  priceText4?: string | null;
}

export interface SeminarCardProps {
  title?: string | null;
  subtitle?: string | null;
  category?: string | null;
  description?: string | null;
  location?: string | null;
  time?: string | null;
  instructors?: string | Array<string> | null;
  /** Image paths relative to the REST API base, e.g. `/seminar/<id>/images/<n>` */
  images?: Array<string>;
  id?: string | null;
  titleLink?: string;
  singleView?: boolean;
  price?: SeminarPrice;
  signUpLink?: string;
  isWaitlist?: boolean;
  isParticipant?: boolean;
  isSignedUp?: boolean;
  isParticipationCanceled?: boolean;
  isLecturer?: boolean;
  isBookedOut?: boolean;
  isAlmostBookedOut?: boolean;
  signUpLinkCaption?: string;
  signUpLinkLogo?: string;
  cancelSignUpCaption?: string;
  cancelSignUpLogo?: string;
  onCancelSignUpClick?: () => void;
  tabbed?: boolean;
  registrationDate?: string | null;
  showSignalLight?: boolean;
}

export const SeminarCard: React.FC<SeminarCardProps> = ({
  title,
  category,
  description,
  location,
  time,
  instructors,
  id,
  titleLink,
  singleView,
  subtitle,
  signUpLink,
  isSignedUp,
  isParticipationCanceled,
  isLecturer,
  isParticipant,
  isWaitlist,
  isBookedOut,
  isAlmostBookedOut,
  signUpLinkCaption = 'Anmelden',
  signUpLinkLogo = 'AcademicCap',
  cancelSignUpCaption = 'Abmelden',
  cancelSignUpLogo = 'XMark',
  onCancelSignUpClick,
  images,
  tabbed,
  registrationDate,
  showSignalLight,
}) => {
  const isMultipleInstructors =
    Array.isArray(instructors) && instructors.length > 1;

  let flagComponent = null;
  if (isLecturer) {
    flagComponent = (
      <CornerFlag>
        Sie sind Dozent{' '}
        <HeroIcon name="AcademicCap" className="h-5 w-5 text-black" />
      </CornerFlag>
    );
  } else if (isParticipant) {
    flagComponent = (
      <CornerFlag>
        Sie nehmen Teil{' '}
        <HeroIcon name="Ticket" className="h-5 w-5 text-black" />
      </CornerFlag>
    );
  } else if (isWaitlist) {
    flagComponent = (
      <CornerFlag>
        Sie sind auf der Warteliste{' '}
        <HeroIcon name="QueueList" className="h-5 w-5 text-black" />
      </CornerFlag>
    );
  } else if (isSignedUp) {
    flagComponent = (
      <CornerFlag>
        Sie sind angemeldet{' '}
        <HeroIcon name="ShieldCheck" className="h-5 w-5 text-black" />
      </CornerFlag>
    );
  } else if (isParticipationCanceled) {
    flagComponent = (
      <CornerFlag>
        Sie sind abgemeldet{' '}
        <HeroIcon name="ShieldExclamation" className="h-5 w-5 text-black" />
      </CornerFlag>
    );
  }

  return (
    <div
      className={`seminar-card relative flex w-full flex-col bg-white ${tabbed ? '' : 'overflow-hidden rounded-md border border-gray-200'}`}
    >
      {Boolean(images?.length) && (
        <ScrollView
          style={{
            width: '100%',
            aspectRatio: '21 / 9',
            maxHeight: 408,
            border: '0px',
          }}
          automaticViewChange={false}
        >
          {images?.map((item, index) => {
            return (
              <img
                key={'img' + index}
                src={restApiUrl(item)}
                className="h-full max-h-[408px] w-full object-cover object-center"
              />
            );
          })}
        </ScrollView>
      )}
      {flagComponent}
      <div className="p-6 md:p-8">
        {category && (
          <span className="text-sm font-medium leading-6 text-bts-theme-primary">
            {category}
          </span>
        )}
        {title && (
          <h2 className={`${category ? 'mt-4' : ''}`}>
            {titleLink ? <Link to={titleLink}>{title}</Link> : title}
          </h2>
        )}
        {subtitle && (
          <h3 className="leading-none text-bts-theme-text-lighter">
            {subtitle}
          </h3>
        )}
        {singleView && id ? (
          <div className="flex justify-between">
            <InlineDetail
              icon="Hashtag"
              label="Seminarnummer"
              caption={id}
              title={'Ort'}
            />
          </div>
        ) : (
          (location || time) && (
            <div className="grid grid-cols-2 justify-between gap-4">
              {time && (
                <InlineDetail
                  icon="Clock"
                  caption={truncateStringAtWhitespace(time, 120)}
                  title={'Datum und Uhrzeit'}
                />
              )}
              {location && (
                <InlineDetail
                  icon="MapPin"
                  caption={truncateStringAtWhitespace(location, 120)}
                  title={'Ort'}
                  className="justify-end"
                />
              )}
            </div>
          )
        )}
        {(Boolean(description) || Boolean(signUpLink)) &&
          (singleView ? (
            <>
              <article
                className={`prose prose-zinc flex max-w-none flex-col gap-4 text-base leading-7 ${category || title || subtitle || id || location || time ? 'mt-6' : ''}`}
                {...(singleView
                  ? {
                      dangerouslySetInnerHTML: {
                        __html: description as string,
                      },
                    }
                  : {})}
              ></article>
              {signUpLink && (
                <div className="mt-8 space-x-2">
                  <Link
                    to={signUpLink}
                    // className="k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md text-red"
                    className="k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md !border-bts-theme-primary !bg-bts-theme-primary !text-white hover:!border-bts-theme-primary-darker hover:!bg-bts-theme-primary-darker"
                  >
                    {signUpLinkLogo && (
                      <HeroIcon
                        name={signUpLinkLogo}
                        className="inline-block h-5 w-5"
                      />
                    )}
                    {signUpLinkCaption}
                  </Link>
                  {onCancelSignUpClick && (
                    <Button
                      onClick={onCancelSignUpClick}
                      themeColor={'secondary'}
                      className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
                    >
                      {cancelSignUpLogo && (
                        <HeroIcon
                          name={cancelSignUpLogo}
                          className="inline-block h-5 w-5"
                        />
                      )}
                      {cancelSignUpCaption}
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <p
              className="mt-6 hidden text-base leading-7 text-zinc-500 md:block"
              dangerouslySetInnerHTML={{
                __html: truncateStringAtWhitespace(
                  description as string,
                  500,
                  ' ',
                ),
              }}
            />
          ))}
        {!singleView && (instructors || id) && (
          <dl className="mt-8 divide-y border-y">
            {instructors && instructors?.length > 0 && (
              <div className="flex items-start justify-between py-2">
                <dt className="flex items-center gap-1 font-semibold">
                  <HeroIcon name="UserCircle" className="h-5 w-5 shrink-0" />
                  {`Referierende:`}
                </dt>
                <dd className="text-right">
                  {isMultipleInstructors
                    ? instructors.map((name, i) => (
                        <Fragment key={i}>
                          {name}
                          <br />
                        </Fragment>
                      ))
                    : instructors}
                </dd>
              </div>
            )}
            {id && (
              <div className="flex flex-wrap items-center justify-between py-2">
                <dt className="flex items-center gap-1 font-semibold">
                  <HeroIcon
                    name="InformationCircle"
                    className="h-5 w-5 shrink-0"
                  />
                  Seminarnummer:
                </dt>
                <dd className="ml-auto">{id}</dd>
              </div>
            )}
            {registrationDate && (
              <div className="flex flex-wrap items-center justify-between py-2">
                <dt className="flex items-center gap-1 font-semibold">
                  <HeroIcon name="Bell" className="h-5 w-5 shrink-0" />
                  Anmeldeschluss:
                </dt>
                <dd className="ml-auto">
                  {getFormattedDate(registrationDate)}
                </dd>
              </div>
            )}
            {showSignalLight && (
              <div className="flex flex-wrap items-center justify-between py-2">
                <dt className="flex items-center gap-1 font-semibold">
                  <HeroIcon name="Ticket" className="h-5 w-5 shrink-0" />
                  Teilnehmeranzahl:
                </dt>
                <dd className="ml-auto">
                  <SignalLight
                    isBookedOut={isBookedOut}
                    isAlmostBookedOut={isAlmostBookedOut}
                    registrationDate={registrationDate}
                    withText
                  />
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </div>
  );
};

interface InlineDetailProps {
  icon: string;
  caption: string;
  title?: string;
  label?: string;
  className?: string;
}

const InlineDetail: React.FC<InlineDetailProps> = ({
  icon,
  caption,
  title,
  label,
  className,
}) => {
  return (
    <div
      className={`mt-6 flex items-center text-bts-theme-text-lighter ${className || ''}`}
      title={title}
    >
      <HeroIcon name={icon} className="mr-1 size-6 shrink-0" />
      <div className="text-base font-medium">
        {label && <span>{`${label}: `}</span>}
        {caption}
      </div>
    </div>
  );
};

const CornerFlag: React.FC<PropsWithChildren> = ({ children }) => (
  <span className="absolute right-0 top-0 flex items-center gap-2 rounded-bl bg-gray-200 p-2">
    {children}
  </span>
);
